import {
  Alert,
  Box,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

const sections = [
  {
    key: "duplicateApplicantApplications",
    title: "Υπάρχει 2η αίτηση για τον ίδιο αιτούντα",
  },
  {
    key: "applicantMemberships",
    title: "Ο αιτών είναι μέλος σε άλλη αίτηση",
  },
  {
    key: "husbandMemberships",
    title: "Ο σύζυγος/πατέρας είναι σε άλλη αίτηση",
  },
  {
    key: "childMemberships",
    title: "Τα τέκνα είναι σε άλλη αίτηση",
  },
];

const hasValue = (value) => value !== undefined && value !== null && value !== "";

const formatDate = (value) => {
  if (!hasValue(value)) return "-";
  if (typeof value === "object") {
    return value.dateFormatted ?? value.date ?? "-";
  }
  return value;
};

const getApplicationHref = (applicationId, applicationRoute) =>
  hasValue(applicationId)
    ? `/lae-miteres/${applicationRoute}/${applicationId}`
    : null;

const getRows = (person) => {
  const applications = Array.isArray(person?.applications)
    ? person.applications
    : [];

  if (applications.length === 0) {
    return [{ person, application: null }];
  }

  return applications.map((application) => ({ person, application }));
};

const CrossApplicationMembershipWarnings = ({
  warnings,
  applicationRoute = "application",
}) => {
  const visibleSections = sections
    .map((section) => ({
      ...section,
      entries: Array.isArray(warnings?.[section.key])
        ? warnings[section.key]
        : [],
    }))
    .filter((section) => section.entries.length > 0);

  const nonWhitelistHusbands = Array.isArray(warnings?.nonWhitelistHusbands)
    ? warnings.nonWhitelistHusbands
    : [];

  if (visibleSections.length === 0 && nonWhitelistHusbands.length === 0)
    return null;

  return (
    <Box sx={{ mb: 2 }}>
      {nonWhitelistHusbands.length > 0 && (
        <Alert
          severity="warning"
          sx={{
            mb: visibleSections.length > 0 ? 2 : 0,
            alignItems: "flex-start",
            "& .MuiAlert-message": { width: "100%" },
          }}
        >
          <Typography fontWeight={700} mb={1}>
            ΠΡΟΣΟΧΗ: Έτεροι γονείς εκτός Μητρώου Αγροτών περιόδου αίτησης
          </Typography>
          <Typography mb={2}>
            Οι παρακάτω έτεροι γονείς δεν βρέθηκαν στη whitelist με τον
            συνδυασμό ΑΜΚΑ και Α.Μ.ΟΓΑ/ΕΦΚΑ για τη συγκεκριμένη περίοδο.
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead sx={{ "& .MuiTableCell-head": { fontWeight: 700 } }}>
                <TableRow>
                  <TableCell>Ονοματεπώνυμο</TableCell>
                  <TableCell>ΑΜΚΑ</TableCell>
                  <TableCell>Α.Μ.ΟΓΑ/ΕΦΚΑ</TableCell>
                  <TableCell>Περίοδος</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {nonWhitelistHusbands.map((person) => (
                  <TableRow key={person?.personMemberId ?? person?.amka}>
                    <TableCell>{person?.personName ?? "-"}</TableCell>
                    <TableCell>{person?.amka ?? "-"}</TableCell>
                    <TableCell>{person?.ama ?? "-"}</TableCell>
                    <TableCell>{person?.season ?? person?.seasonId ?? "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Alert>
      )}

      {visibleSections.length > 0 && (
        <Alert
          severity="warning"
          sx={{
            alignItems: "flex-start",
            "& .MuiAlert-message": { width: "100%" },
          }}
        >
          <Typography fontWeight={700} mb={2}>
            ΠΡΟΣΟΧΗ: Βρέθηκαν πιθανές συσχετίσεις με άλλες αιτήσεις της ίδιας
            περιόδου.
          </Typography>

          {visibleSections.map((section) => (
            <Box key={section.key} sx={{ mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} mb={1}>
                {section.title}
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead sx={{ "& .MuiTableCell-head": { fontWeight: 700 } }}>
                    <TableRow>
                      <TableCell>Ονοματεπώνυμο</TableCell>
                      <TableCell>ΑΜΚΑ</TableCell>
                      <TableCell>Α/Α αίτησης</TableCell>
                      <TableCell>Τύπος αίτησης</TableCell>
                      <TableCell>Κατάσταση</TableCell>
                      <TableCell>Ημ. υποβολής</TableCell>
                      <TableCell>Βρέθηκε ως</TableCell>
                      <TableCell align="right"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {section.entries.flatMap((person) =>
                      getRows(person).map(({ person, application }, index) => {
                        const href = getApplicationHref(
                          application?.applicationId,
                          applicationRoute
                        );

                        return (
                          <TableRow
                            key={`${person?.personMemberId ?? "person"}-${application?.applicationId ?? "application"
                              }-${index}`}
                          >
                            <TableCell>{person?.personName ?? "-"}</TableCell>
                            <TableCell>{person?.amka ?? "-"}</TableCell>
                            <TableCell>
                              {hasValue(application?.protocolNumber)
                                ? `#${application.protocolNumber}`
                                : "-"}
                            </TableCell>
                            <TableCell>{application?.applicationType ?? "-"}</TableCell>
                            <TableCell>{application?.applicationStatus ?? "-"}</TableCell>
                            <TableCell>
                              {application?.submittedAtFormatted ??
                                formatDate(application?.submittedAt)}
                            </TableCell>
                            <TableCell>{application?.matchedAs ?? "-"}</TableCell>
                            <TableCell align="right">
                              {href ? (
                                <Link href={href} target="_blank" rel="noopener noreferrer">
                                  Προβολή
                                </Link>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))}
        </Alert>
      )}
    </Box>
  );
};

export default CrossApplicationMembershipWarnings;
